'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Camera, Check, Leaf, RefreshCw, ScanSearch } from 'lucide-react'

import { AnalysisResultFrame, AnalysisVerdictHeader, ANALYSIS_SECTION_CARD } from '@/components/analysis/analysis-result-primitives'
import { InconclusivePlanAsk } from '@/components/analysis/inconclusive-plan-ask'
import { CuratedProductPicksCard, type CuratedProductRoleTone } from '@/components/analysis/curated-product-picks-card'
import { ResultUpgradeCard, type ResultUpgradeCardCopy } from '@/components/home/result-upgrade-card'
import { ResultStickySaveBar } from '@/components/home/result-sticky-save-bar'
import { SavedPlanActionCard } from '@/components/home/saved-plan-action-card'
import { AppDownloadCTA, AppDownloadTextLink } from '@/components/ui/app-download-cta'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { resolveWeedAlias } from '@/data/weed-registry'
import { useOfferedActionsSnapshot } from '@/hooks/use-offered-actions-snapshot'
import { useVisibleImpression } from '@/hooks/use-visible-impression'
import { evidenceLines } from '@/lib/analysis/weed-evidence-lines'
import { canAccumulateWeedPhotos } from '@/lib/analysis/weed-photo-accumulation'
import { analytics, trackEvent, type ResultNextActionId, type ResultStateId } from '@/lib/analytics'
import { savedPlanEventProperties } from '@/lib/saved-plan-analytics'
import { safeJson } from '@/lib/safe-json'
import { cn } from '@/lib/utils'
import type { WeedIdentificationResult } from '@/lib/types/weed-identification'

type ShelfPick = {
  productId: string
  asin: string
  name: string
  description: string
  category: string
  price: string
  imageUrl?: string
  affiliateUrl: string
  productRole: 'immediate_control' | 'tool' | 'prevention_followup'
  position: number
  reason?: string
  pickSource: string
  hostGrassVerified: boolean
}

const ROLE_LABELS: Record<ShelfPick['productRole'], string> = {
  immediate_control: 'Best immediate control',
  tool: 'Removal tool',
  prevention_followup: 'Prevention and follow-up',
}

const ROLE_TONES: Record<ShelfPick['productRole'], CuratedProductRoleTone> = {
  immediate_control: 'danger',
  tool: 'warning',
  prevention_followup: 'info',
}

const WEED_CAPTURE_COPY: ResultUpgradeCardCopy = {
  emailCtaLabel: 'Email me this plan',
  emailPlaceholder: 'Enter your email',
  emailAriaLabel: 'Email address',
  emailDividerLabel: 'or email me this plan',
  emailRevealLabel: 'get this plan by email',
  googleLabel: 'Save my control plan with Google',
  appleLabel: 'Save my control plan with Apple',
}

// Module-scoped so a remount of the same settled result does not re-emit the
// state impression. Keyed `${attemptId}:${state}` like the other impressions.
const RESULT_STATE_IMPRESSIONS = new Set<string>()

function eventBase(
  result: WeedIdentificationResult,
  funnelId: string,
  analysisId: string,
  entry: 'dedicated' | 'identify_recovery',
) {
  return {
    funnel_id: funnelId,
    analysis_id: analysisId,
    root_analysis_id: result.lineage?.rootAnalysisId,
    weed_id: result.weedId || undefined,
    platform: 'web',
    result_quality: result.imageQuality,
    model: result.model,
    prompt_version: result.promptVersion,
    taxonomy_version: result.taxonomyVersion,
    pipeline_version: result.pipelineVersion,
    result_confidence: result.displayConfidence,
    recovery_kind: result.lineage?.kind,
    usage_exempt: result.lineage?.usageExempt,
    weed_entry: entry,
    experiment_id: 'weed-id-v1',
  }
}

function ProductShelf({
  picks,
  result,
  funnelId,
  analysisId,
  accessToken,
  entry,
}: {
  picks: ShelfPick[]
  result: WeedIdentificationResult
  funnelId: string
  analysisId: string
  accessToken: string
  entry: 'dedicated' | 'identify_recovery'
}) {
  const shelfRef = useRef<HTMLElement | null>(null)
  const tracked = useRef(false)

  useEffect(() => {
    const node = shelfRef.current
    if (!node || picks.length === 0) return
    const observer = new IntersectionObserver(([visible]) => {
      if (!visible.isIntersecting || tracked.current) return
      tracked.current = true
      const base = eventBase(result, funnelId, analysisId, entry)
      trackEvent('weed_product_shelf_viewed', {
        ...base,
        product_count: picks.length,
        host_grass_verified: result.hostGrassVerified,
      })
      picks.forEach((pick) => trackEvent('affiliate_product_shown', {
        ...base,
        placement: 'weed_identifier_result',
        product_id: pick.productId,
        asin: pick.asin,
        product_role: pick.productRole,
        position: pick.position,
        host_grass_verified: pick.hostGrassVerified,
        pick_source: pick.pickSource,
      }))
      void fetch('/api/weed-identifier/attribution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funnelId,
          analysisId,
          resultAccessToken: accessToken,
          milestone: 'product_shelf_viewed',
        }),
        keepalive: true,
      })
      observer.disconnect()
    }, { threshold: 0.5 })
    observer.observe(node)
    return () => observer.disconnect()
  }, [accessToken, analysisId, entry, funnelId, picks, result])

  if (picks.length === 0) return null
  const items = picks.map((pick) => ({
    id: pick.productId,
    name: pick.name,
    note: pick.reason,
    imageUrl: pick.imageUrl,
    affiliateUrl: pick.affiliateUrl,
    roleLabel: ROLE_LABELS[pick.productRole],
    roleTone: ROLE_TONES[pick.productRole],
  }))

  return <CuratedProductPicksCard
    ref={shelfRef}
    headingId="weed-product-picks-heading"
    title={`Best next steps for ${result.weedName}`}
    description={result.hostGrassVerified
      ? 'Matched to this weed, your confirmed grass, and label safety.'
      : 'Recommended from the weed in your photo. Check each product label for your grass and conditions.'}
    items={items}
    testId="weed-product-shelf"
    onProductClick={(_item, index) => {
      const pick = picks[index]
      if (!pick) return
      trackEvent('affiliate_product_clicked', {
        ...eventBase(result, funnelId, analysisId, entry),
        placement: 'weed_identifier_result',
        product_id: pick.productId,
        asin: pick.asin,
        product_role: pick.productRole,
        position: pick.position,
        host_grass_verified: pick.hostGrassVerified,
        pick_source: pick.pickSource,
      })
      void fetch('/api/weed-identifier/attribution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ funnelId, analysisId, resultAccessToken: accessToken, milestone: 'product_clicked' }),
        keepalive: true,
      })
    }}
  />
}

export function WeedResultPresentation({
  result,
  analysisId,
  accessToken,
  funnelId,
  entry = 'dedicated',
  onRefine,
  onRetake,
  planLawnId,
  zipCode,
  attemptId,
}: {
  result: WeedIdentificationResult
  analysisId: string
  accessToken: string
  funnelId: string
  entry?: 'dedicated' | 'identify_recovery'
  /** Falls back to the analysis id on surfaces that do not thread an attempt. */
  attemptId?: string
  /** Opens the accumulating refinement step, keeping the photos already sent. */
  onRefine?: () => void
  /** Starts over with a fresh photo when accumulation cannot help. */
  onRetake?: () => void
  /** Personalizes the seasonal-plan ask on the no-verdict branches. */
  zipCode?: string | null
  /** Undefined means the embedding surface does not know. Null is a confirmed
      history-only save with no lawn-scoped Dashboard tasks. */
  planLawnId?: string | null
}) {
  const { user } = useAuth()
  const [picks, setPicks] = useState<ShelfPick[]>([])
  const [productState, setProductState] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle')
  const [productEmptyReason, setProductEmptyReason] = useState<string | null>(null)
  const [productRetry, setProductRetry] = useState(0)
  const [email, setEmail] = useState('')
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [feedbackSent, setFeedbackSent] = useState(false)
  const captureTracked = useRef(false)
  const captureRef = useRef<HTMLElement | null>(null)
  const savedPlanContext = useMemo(() => ({
    analysisId,
    mode: 'weed' as const,
    planType: 'control' as const,
    outcome: result.outcome,
    entryPath: entry === 'identify_recovery' ? 'identify_recovery' : 'weed_identifier_result',
    funnelId,
    subject: result.weedName || undefined,
  }), [analysisId, entry, funnelId, result.outcome, result.weedName])

  // `failed` shares the rescan branch with `inconclusive`, so it reports as one
  // state: the user sees the same screen and the same offered actions.
  const resultState: ResultStateId = result.outcome === 'supported'
    ? 'weed_supported'
    : result.outcome === 'unsupported'
      ? 'weed_unsupported'
      : 'weed_inconclusive'

  // Only `full` weeds have a published page. `known` weeds carry an empty
  // guideSlug in WEED_REGISTRY, and an unconfirmed best guess is resolved
  // through the registry because the stored row has no slug of its own.
  const guide = useMemo(() => {
    if (result.outcome === 'supported') {
      const slug = result.supportLevel === 'known' ? '' : result.guideSlug?.trim()
      return slug && result.weedName ? { slug, name: result.weedName } : null
    }
    const entryMatch = resolveWeedAlias(result.bestGuessName)
    const slug = entryMatch?.guideSlug?.trim()
    return entryMatch && slug ? { slug, name: entryMatch.displayName } : null
  }, [result.bestGuessName, result.guideSlug, result.outcome, result.supportLevel, result.weedName])

  const canAccumulate = canAccumulateWeedPhotos(result)
  const offeredActions = useMemo<ResultNextActionId[]>(() => {
    if (result.outcome === 'supported') {
      return [
        user ? 'open_control_steps' : 'save_result',
        ...(guide ? ['read_weed_guide'] : []),
        'view_pricing',
      ]
    }
    if (result.outcome === 'unsupported') {
      return [...(guide ? ['read_weed_guide'] : []), 'browse_weed_library', 'save_result']
    }
    return [
      canAccumulate ? 'add_supporting_photo' : 'retake_photo',
      ...(guide ? ['read_weed_guide'] : []),
      'save_result',
    ]
  }, [canAccumulate, guide, result.outcome, user])

  // Auth settling flips index 0 (save_result -> open_control_steps), so the
  // impression and every click have to read one frozen copy of the list.
  const { snapshotOfferedActions, positionOf } = useOfferedActionsSnapshot(offeredActions)

  const attemptKey = attemptId ?? analysisId
  const authState = user ? 'authenticated' as const : 'anonymous' as const
  const analyticsSource = entry === 'identify_recovery' ? 'identify_recovery' : 'weed_identifier_result'

  useVisibleImpression(RESULT_STATE_IMPRESSIONS, true, `${attemptKey}:${resultState}`, () => {
    analytics.resultStateShown({
      state: resultState,
      mode: 'weed',
      attemptId: attemptKey,
      funnelId,
      analysisId,
      offeredActions: snapshotOfferedActions(),
      confidence: result.confidence,
      displayConfidence: result.displayConfidence,
      authState,
      source: analyticsSource,
    })
  })

  const trackNextAction = (action: ResultNextActionId) => {
    analytics.resultNextActionClicked({
      action,
      fromState: resultState,
      mode: 'weed',
      attemptId: attemptKey,
      funnelId,
      analysisId,
      position: positionOf(action),
      authState,
      source: analyticsSource,
    })
  }

  useEffect(() => {
    if (result.outcome !== 'supported') {
      setPicks([])
      setProductState('idle')
      setProductEmptyReason(null)
      return
    }
    let cancelled = false
    setProductState('loading')
    void fetch('/api/weed-identifier/product-picks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analysisId, resultAccessToken: accessToken }),
    }).then(async (response) => {
      const data = await safeJson<{ picks?: ShelfPick[]; fallbackReason?: string }>(response)
      if (cancelled) return
      if (!response.ok || !data || !Array.isArray(data.picks)) {
        setPicks([])
        setProductState('error')
        setProductEmptyReason(null)
        return
      }
      setPicks(data.picks)
      setProductEmptyReason(data.picks.length === 0 ? data.fallbackReason ?? null : null)
      setProductState(data.picks.length > 0 ? 'ready' : 'empty')
    }).catch(() => {
      if (cancelled) return
      setPicks([])
      setProductState('error')
      setProductEmptyReason(null)
    })
    return () => { cancelled = true }
  }, [accessToken, analysisId, productRetry, result.outcome])

  useEffect(() => {
    const node = captureRef.current
    if (result.outcome !== 'supported' || !node || captureTracked.current) return
    const observer = new IntersectionObserver(([visible]) => {
      if (!visible.isIntersecting || captureTracked.current) return
      captureTracked.current = true
      trackEvent('weed_result_capture_shown', savedPlanEventProperties(
        savedPlanContext,
        eventBase(result, funnelId, analysisId, entry),
      ))
      observer.disconnect()
    }, { threshold: 0.35 })
    observer.observe(node)
    return () => observer.disconnect()
  }, [analysisId, entry, funnelId, result, savedPlanContext])

  useEffect(() => {
    if (entry !== 'identify_recovery' || result.outcome !== 'supported') return
    const previousTitle = document.title
    document.title = `${result.weedName} identified | What Grass Is This`
    return () => {
      document.title = previousTitle
    }
  }, [entry, result.outcome, result.weedName])

  const submitEmail = async () => {
    const normalizedEmail = email.trim()
    if (emailState === 'sending') return
    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setEmailState('error')
      return
    }
    // The email lane of the same `save_result` offer the OAuth lane reports
    // from `startSignup`. Fired on the valid attempt, like the OAuth one, so
    // the two lanes are comparable.
    trackNextAction('save_result')
    setEmailState('sending')
    try {
      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          context: 'weed_identifier_result',
          analysisId,
          resultAccessToken: accessToken,
          funnelId,
        }),
      })
      const data = await safeJson<{ emailSent?: boolean }>(response)
      if ((response.ok || response.status === 409) && data?.emailSent !== false) {
        setEmailState('sent')
        trackEvent('weed_result_email_captured', eventBase(result, funnelId, analysisId, entry))
        return
      }
      setEmailState('error')
    } catch {
      setEmailState('error')
    }
  }

  const submitFeedback = async (feedback: 'confirmed' | 'still_unsure' | 'not_a_weed') => {
    const response = await fetch('/api/analysis/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analysisId, resultAccessToken: accessToken, feedback, funnelId }),
    })
    if (response.ok) {
      setFeedbackSent(true)
      trackEvent('weed_id_feedback_submitted', { ...eventBase(result, funnelId, analysisId, entry), feedback })
    }
  }

  const signupReturn = `/weed-identifier?analysis_id=${analysisId}&access=${encodeURIComponent(accessToken)}&funnel_id=${funnelId}`
  const refinementReturn = `${signupReturn}&refine=1`
  const startSignup = async () => {
    trackNextAction('save_result')
    trackEvent('weed_result_signup_started', eventBase(result, funnelId, analysisId, entry))
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), 2_000)
    try {
      await fetch('/api/weed-identifier/attribution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funnelId,
          analysisId,
          resultAccessToken: accessToken,
          milestone: 'signup_started',
        }),
        keepalive: true,
        signal: controller.signal,
      })
    } catch {
      // Attribution must not permanently block account creation when the
      // network is unavailable. OAuth resumes after this bounded attempt.
    } finally {
      window.clearTimeout(timeoutId)
    }
  }

  // An unsupported weed is a catalog gap, not a photo problem: the model read the
  // plant confidently and it simply is not in WEED_REGISTRY. Routing these into the
  // rescan card asks for a photo that provably cannot change the outcome, so they
  // get their own terminal card with a real next step.
  if (result.outcome === 'unsupported') {
    const bestGuess = result.bestGuessName?.trim() || null
    return (
      <section id="results-section" className="relative scroll-mt-24 overflow-x-hidden px-3 py-6 md:px-10 md:py-8">
        <AnalysisResultFrame cardClassName="border-sky-200/80">
          <p role="status" aria-live="polite" className="sr-only">
            {bestGuess
              ? `Best guess: ${bestGuess}. This weed is outside our treatment catalog, so we cannot confirm the identification.`
              : 'This weed is outside our current catalog, so we cannot confirm an identification.'}
          </p>
          <AnalysisVerdictHeader
            eyebrow={bestGuess ? 'Best guess, outside our treatment catalog' : 'Outside our current weed catalog'}
            title={bestGuess ? <>This looks like {bestGuess}.</> : "We don't cover this weed yet."}
            description={bestGuess
              ? 'That is our best read, not a confirmed ID. This weed is not in the catalog we verify and treat against yet, so another photo will not change it.'
              : "Another photo won't change this one. The plant looks like a real lawn weed, it just isn't in our identification catalog yet."}
            icon={<ScanSearch className="h-3.5 w-3.5" />}
            tone="uncertain"
          />
          <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-[.9fr_1.1fr] md:items-stretch">
            <div className="relative min-h-56 overflow-hidden rounded-2xl bg-gray-100 md:min-h-80">
              {result.image ? <Image src={result.image} alt="Photo submitted for weed identification" fill sizes="(min-width: 768px) 42vw, 100vw" className="object-cover" /> : <Camera className="absolute inset-0 m-auto h-10 w-10 text-gray-400" />}
            </div>
            <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-5 sm:p-6">
              {result.analysis || bestGuess ? (
                <>
                  <h2 className="text-lg font-extrabold text-gray-900">What we could tell</h2>
                  {bestGuess ? (
                    <p className="mt-3 text-sm font-semibold leading-6 text-gray-900">
                      Most likely: {bestGuess}
                    </p>
                  ) : null}
                  {result.analysis ? <p className="mt-3 text-sm leading-6 text-gray-700">{result.analysis}</p> : null}
                  <p className="mt-3 text-xs leading-5 text-gray-500">
                    That is an unconfirmed read, not an identification. We only confirm weeds we cover.
                  </p>
                </>
              ) : (
                <h2 className="text-lg font-extrabold text-gray-900">We could not match this plant to a weed we cover.</h2>
              )}
              {guide ? (
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button asChild className="min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">
                    <Link href={`/weeds/${guide.slug}`} onClick={() => trackNextAction('read_weed_guide')}>
                      Read the {guide.name} guide
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="min-h-11 rounded-xl border-green-300 bg-white font-bold text-green-700 hover:bg-green-50">
                    <Link href="/weeds" onClick={() => trackNextAction('browse_weed_library')}>Browse the weed library</Link>
                  </Button>
                </div>
              ) : (
                <Button asChild className="mt-6 min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">
                  <Link href="/weeds" onClick={() => trackNextAction('browse_weed_library')}>Browse the weed library</Link>
                </Button>
              )}
            </div>
          </div>
          <div className="mx-auto mt-5 max-w-4xl">
            <InconclusivePlanAsk
              source="weed_unsupported"
              zipCode={zipCode ?? null}
              redirectTo={user ? undefined : signupReturn}
              onCaptured={() => trackNextAction('save_result')}
              onBeforeOAuth={startSignup}
            />
          </div>
          <div className="mt-4 text-center">
            <AppDownloadTextLink placement="weed_identifier_unsupported" />
          </div>
        </AnalysisResultFrame>
      </section>
    )
  }

  if (result.outcome !== 'supported') {
    const outcomeLabel = result.outcome === 'failed'
      ? 'Identification could not finish'
      : 'Not enough evidence yet'
    // Accumulation keeps the photos already sent and adds a view. When the
    // shortfall is not an evidence gap, the honest action is a fresh photo.
    const recoveryLabel = canAccumulate ? 'Add another photo' : 'Take another photo'
    const recoveryAction: ResultNextActionId = canAccumulate ? 'add_supporting_photo' : 'retake_photo'

    return (
      <section id="results-section" className="relative scroll-mt-24 overflow-x-hidden px-3 py-6 md:px-10 md:py-8">
        <AnalysisResultFrame cardClassName="border-amber-200/80">
          <p role="status" aria-live="polite" className="sr-only">
            {`${outcomeLabel}. ${result.recommendedNextPhoto || 'Take another photo to continue.'}`}
          </p>
          <AnalysisVerdictHeader
            eyebrow={outcomeLabel}
            title="A safer answer is to rescan."
            description={result.recommendedNextPhoto || 'We could not get enough visible evidence from this photo.'}
            icon={<Camera className="h-3.5 w-3.5" />}
            tone="uncertain"
          />
          <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-[.9fr_1.1fr] md:items-stretch">
            <div className="relative min-h-56 overflow-hidden rounded-2xl bg-gray-100 md:min-h-80">
              {result.image ? <Image src={result.image} alt="Photo submitted for weed identification" fill sizes="(min-width: 768px) 42vw, 100vw" className="object-cover" /> : <Camera className="absolute inset-0 m-auto h-10 w-10 text-gray-400" />}
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6">
              {result.bestGuessName?.trim() ? (
                <div className="mb-4 rounded-xl bg-white/70 px-4 py-3 ring-1 ring-amber-200/70">
                  <p className="text-sm leading-6 text-gray-800">
                    <strong className="font-extrabold text-gray-900">Leading guess so far: {result.bestGuessName.trim()}.</strong>{' '}
                    Not confirmed yet. The photos below decide it.
                  </p>
                  {guide ? (
                    <Link
                      href={`/weeds/${guide.slug}`}
                      onClick={() => trackNextAction('read_weed_guide')}
                      className="mt-2 inline-block text-sm font-bold text-green-700 underline underline-offset-2"
                    >
                      Read the {guide.name} guide
                    </Link>
                  ) : null}
                </div>
              ) : null}
              <h2 className="text-lg font-extrabold text-gray-900">What we still need to see</h2>
              {evidenceLines(result.missingEvidence).length > 0 ? (
                <ul className="mt-3 space-y-3">
                  {evidenceLines(result.missingEvidence).map((item) => <li key={item} className="flex gap-3 text-sm leading-6 text-gray-700"><ScanSearch className="mt-1 h-4 w-4 shrink-0 text-amber-700" />{item}</li>)}
                </ul>
              ) : <p className="mt-3 text-sm leading-6 text-gray-700">Add a sharp close-up of the leaf, stem, or seed head requested above.</p>}
              {canAccumulate ? (
                onRefine ? (
                  <Button onClick={() => { trackNextAction(recoveryAction); onRefine() }} className="mt-6 min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">{recoveryLabel}</Button>
                ) : (
                  <Button asChild className="mt-6 min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">
                    <Link href={refinementReturn} onClick={() => trackNextAction(recoveryAction)}>{recoveryLabel}</Link>
                  </Button>
                )
              ) : onRetake ? (
                <Button onClick={() => { trackNextAction(recoveryAction); onRetake() }} className="mt-6 min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">{recoveryLabel}</Button>
              ) : (
                <Button asChild className="mt-6 min-h-11 rounded-xl bg-green-600 font-bold hover:bg-green-700">
                  <Link href="/weed-identifier" onClick={() => trackNextAction(recoveryAction)}>{recoveryLabel}</Link>
                </Button>
              )}
              {/* Bounded by resolveRescanExemption: a 60-minute window from the root
                  scan, at most 3 exempt refinements, and at least one genuinely new
                  photo. Stating it unconditionally promised more than the server honors. */}
              {canAccumulate ? (
                <p className="mt-3 text-xs leading-5 text-gray-500">Adding a photo within an hour of this scan does not use another Diagnosis credit, for up to three follow-ups.</p>
              ) : null}
            </div>
          </div>
          <div className="mx-auto mt-5 max-w-4xl">
            <InconclusivePlanAsk
              source="weed_inconclusive"
              zipCode={zipCode ?? null}
              redirectTo={user ? undefined : signupReturn}
              onCaptured={() => trackNextAction('save_result')}
              onBeforeOAuth={startSignup}
            />
          </div>
          <div className="mt-4 text-center">
            <AppDownloadTextLink placement="weed_identifier_inconclusive" />
          </div>
        </AnalysisResultFrame>
      </section>
    )
  }

  return (
    <section id="results-section" className="relative scroll-mt-24 overflow-x-hidden px-3 py-6 md:px-10 md:py-8">
      <AnalysisResultFrame>
        <p role="status" aria-live="polite" className="sr-only">
          {`Weed identified as ${result.weedName}, ${result.displayConfidence} percent confidence.`}
        </p>
        <AnalysisVerdictHeader
          eyebrow="Weed identified"
          title={<>{result.weedName}, most likely.</>}
          icon={<Leaf className="h-3.5 w-3.5" />}
          chips={(
            <>
              <span className="inline-flex rounded-full bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-green-700 ring-1 ring-inset ring-green-200">{result.weedClass}</span>
              <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-600 ring-1 ring-inset ring-black/[0.08]">{result.displayConfidence}% confidence</span>
              <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-600 ring-1 ring-inset ring-black/[0.08]">{result.imageQuality} photo</span>
            </>
          )}
        />

        <div className="grid min-w-0 items-start gap-6 md:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
          <div className="min-w-0">
            <div className="relative h-56 w-full overflow-hidden rounded-2xl bg-gray-100 ring-1 ring-black/[0.04] md:h-auto md:aspect-square lg:aspect-[4/3]">
              {result.image ? <Image src={result.image} alt={`Photo identified as ${result.weedName}`} fill priority sizes="(min-width: 1024px) 48vw, 100vw" className="object-cover" /> : <Leaf className="absolute inset-0 m-auto h-10 w-10 text-green-700" />}
            </div>
            <section className={cn(ANALYSIS_SECTION_CARD, "mt-5 p-5 sm:p-6")}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">Visible evidence</p>
              <h2 className="mt-2 text-xl font-extrabold tracking-tight text-gray-900">Why this looks like {result.weedName}</h2>
              <ul className="mt-4 space-y-3">{result.visualEvidence.map((evidence) => <li key={evidence} className="flex gap-3 text-sm leading-6 text-gray-700"><Check className="mt-1 h-4 w-4 shrink-0 text-green-600" />{evidence}</li>)}</ul>
              {result.candidates[1] ? <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950"><strong>Closest lookalike:</strong> {result.candidates[1].name}. {result.candidates[1].evidence}</div> : null}
            </section>
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            {user ? (
              <SavedPlanActionCard
                eyebrow={planLawnId === null ? 'Result saved' : 'Control plan saved'}
                title={planLawnId === null
                  ? `Your ${result.weedName} result is saved to History`
                  : `Your ${result.weedName} control plan is saved`}
                firstAction={result.immediateAction || `Review the control options for ${result.weedName}.`}
                // A `known` weed is identified but has no curated products and no
                // application notes, so the standard promise would be false for it.
                actionMeta={result.supportLevel === 'known'
                  ? 'Removal and prevention steps are saved with this result. We do not have a full treatment plan for this weed yet, so no products are recommended.'
                  : planLawnId === null
                    ? 'No Dashboard tasks were created because this scan was not attached to a lawn. The ID and control guidance are still saved in History.'
                    : 'Products, application notes, and prevention steps are saved with this result.'}
                analyticsContext={savedPlanContext}
                planDestination="weed_control_result"
                primaryLabel="Open control steps"
                primaryDestination="weed_control_plan"
                onPrimaryAction={() => {
                  trackNextAction('open_control_steps')
                  document.getElementById('weed-control-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                secondaryHref={planLawnId === null ? '/dashboard/history' : undefined}
                secondaryLabel={planLawnId === null ? 'Open history' : undefined}
              />
            ) : (
              <section className="rounded-2xl border border-green-100 bg-green-50/60 p-5 sm:p-6 md:p-8">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">Start here</p>
                <h2 className="mt-2 text-xl font-extrabold tracking-tight text-gray-900">Your best first step</h2>
                <p className="mt-2 text-sm leading-6 text-gray-700 md:text-base">{result.immediateAction}</p>
              </section>
            )}

            {!user ? (
              <section ref={captureRef} id="weed-result-capture" className="scroll-mt-24">
                <div id="care-plan-upgrade" className="scroll-mt-24 rounded-2xl border border-green-200 bg-linear-to-b from-green-50/70 to-white p-5 shadow-sm sm:p-6">
                  <div className="mb-4 text-center">
                    <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">Keep the plan</p>
                    <h2 className="mt-1.5 text-xl font-extrabold tracking-tight text-gray-900">Save my {result.weedName} control plan</h2>
                    <p className="mt-1.5 text-sm leading-5 text-gray-600">Keep the ID, first action, control options, and prevention steps together.</p>
                  </div>

                  {emailState === 'sent' ? <p className="flex items-center justify-center gap-2 rounded-xl bg-green-50 p-4 font-bold text-green-700"><Check className="h-5 w-5" />Sent. Check your inbox.</p> : (
                    <ResultUpgradeCard
                      embedded
                      savePlanOauth
                      askOrder="account_first"
                      email={email}
                      emailSuggestion={null}
                      isSubmitting={emailState === 'sending'}
                      timelineRedirectUrl={signupReturn}
                      savedPlanContext={savedPlanContext}
                      source="weed_identifier_result"
                      copy={WEED_CAPTURE_COPY}
                      onBeforeOAuth={startSignup}
                      onEmailChange={(event) => {
                        setEmail(event.target.value)
                        if (emailState === 'error') setEmailState('idle')
                      }}
                      onEmailKeyPress={(event) => {
                        if (event.key !== 'Enter') return
                        event.preventDefault()
                        void submitEmail()
                      }}
                      onSubmit={() => void submitEmail()}
                      onAcceptSuggestion={() => {}}
                      onDismissSuggestion={() => {}}
                    />
                  )}
                  {emailState === 'error' ? <p role="alert" className="mt-3 text-center text-xs text-red-700">We could not send it. Check the address and try again.</p> : null}
                </div>
              </section>
            ) : null}
          </div>
        </div>

        <div id="weed-control-plan" className="mt-5 scroll-mt-24 space-y-5 md:mt-6">
          <ProductShelf picks={picks} result={result} funnelId={funnelId} analysisId={analysisId} accessToken={accessToken} entry={entry} />
          {productState === 'loading' ? (
            <section aria-live="polite" className={cn(ANALYSIS_SECTION_CARD, 'p-5 text-sm text-gray-600 sm:p-6')}>
              Loading safe product options for this season...
            </section>
          ) : null}
          {productState === 'error' ? (
            <section role="alert" className={cn(ANALYSIS_SECTION_CARD, 'border-amber-200 bg-amber-50/60 p-5 sm:p-6')}>
              <h2 className="font-extrabold text-gray-900">Product recommendations did not load</h2>
              <p className="mt-1 text-sm leading-6 text-gray-700">Your weed ID and non-chemical control guidance are still available. Try the product list again when your connection is stable.</p>
              <Button type="button" variant="outline" className="mt-4 min-h-11 border-amber-300 bg-white font-bold" onClick={() => setProductRetry((value) => value + 1)}>
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Retry products
              </Button>
            </section>
          ) : null}
          {productState === 'empty' ? (
            <section className={cn(ANALYSIS_SECTION_CARD, 'border-amber-200 bg-amber-50/60 p-5 sm:p-6')}>
              <h2 className="font-extrabold text-gray-900">{productEmptyReason === 'product-shelf-disabled' ? 'Product recommendations are not available' : 'No safe product match right now'}</h2>
              <p className="mt-1 text-sm leading-6 text-gray-700">{productEmptyReason === 'product-shelf-disabled'
                ? 'The product shelf is off for this rollout. Your ID, first action, and prevention guidance are still available.'
                : 'Start with the first action above. We are not filling this shelf with an out-of-season or incompatible treatment.'}</p>
            </section>
          ) : null}

          <section className={cn(ANALYSIS_SECTION_CARD, "p-5 sm:p-6 md:p-8")}>
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">Prevent the next patch</p>
            <ul className="mt-3 space-y-3">{result.preventionGuidance.map((item) => <li key={item} className="flex gap-3 text-sm leading-6 text-gray-700"><ArrowRight className="mt-1 h-4 w-4 shrink-0 text-green-600" />{item}</li>)}</ul>
          </section>

          {guide ? (
            <section className={cn(ANALYSIS_SECTION_CARD, 'flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6 md:p-8')}>
              <h2 className="text-lg font-extrabold tracking-tight text-gray-900">Everything else about {guide.name}</h2>
              <Button asChild variant="outline" className="min-h-11 w-full shrink-0 rounded-xl border-green-300 bg-white font-bold text-green-700 hover:bg-green-50 sm:w-auto">
                <Link href={`/weeds/${guide.slug}`} onClick={() => trackNextAction('read_weed_guide')}>
                  Read the {guide.name} guide
                </Link>
              </Button>
            </section>
          ) : null}

          <AppDownloadCTA
            placement="weed_identifier_result"
            variant="card"
            intent="identify_weed"
            headline="Identify a weed from your phone"
            subhead="Use the free iPhone app to scan the weed again, save the result, and keep the right control steps with you."
          />

          <section className="flex flex-col items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-5 text-center sm:flex-row sm:text-left"><div><p className="font-bold text-gray-900">Was this identification useful?</p><p className="text-sm text-gray-500">Your answer prioritizes expert review. It is not treated as a gold label.</p></div>{feedbackSent ? <span className="font-bold text-green-700">Thanks for the signal.</span> : <div className="flex flex-wrap justify-center gap-2"><Button size="sm" variant="outline" onClick={() => submitFeedback('confirmed')}>Looks right</Button><Button size="sm" variant="outline" onClick={() => submitFeedback('still_unsure')}>Still unsure</Button><Button size="sm" variant="outline" onClick={() => submitFeedback('not_a_weed')}>Not a weed</Button></div>}</section>
          <div className="text-center text-sm text-gray-600">Want the recurring plan after you save your lawn? <Link href={`/pricing?funnel_id=${funnelId}`} onClick={() => trackNextAction('view_pricing')} className="font-bold text-green-700 underline underline-offset-2">See Premium</Link></div>
        </div>
      </AnalysisResultFrame>

      {!user ? <ResultStickySaveBar itemName={result.weedName || 'Weed'} kind="weed" entryPath={entry} targetId="care-plan-upgrade" onNextAction={() => trackNextAction('save_result')} /> : null}
    </section>
  )
}
